import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { useHistory } from 'react-router';
import { useKnowledgebase } from '../../Contexts/KnowledgebaseContext';
import { useAuth } from '../../Contexts/AuthContext';
import { getArticle } from '../../Helpers/helpApi';
import FeedbackWidget from './FeedbackWidget';

// --- ImageLightbox ---
function ImageLightbox({ src, alt, open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black' }}>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, color: 'white', zIndex: 1 }}
          aria-label="Close image"
        >
          <CloseIcon />
        </IconButton>
        <Box
          component="img"
          src={src}
          alt={alt || ''}
          sx={{ width: '100%', height: 'auto', display: 'block' }}
        />
      </DialogContent>
    </Dialog>
  );
}

// --- CalloutBlock ---
const CALLOUT_STYLES = {
  tip: { bgcolor: 'info.lighter', borderColor: 'info.main', icon: <InfoIcon fontSize="small" color="info" /> },
  warning: { bgcolor: 'warning.lighter', borderColor: 'warning.main', icon: <WarningIcon fontSize="small" color="warning" /> },
  note: { bgcolor: 'error.lighter', borderColor: 'error.main', icon: <ErrorIcon fontSize="small" color="error" /> },
};

function CalloutBlock({ type = 'tip', content }) {
  const style = CALLOUT_STYLES[type] || CALLOUT_STYLES.tip;
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        p: 1.5,
        my: 1.5,
        borderLeft: 3,
        borderColor: style.borderColor,
        bgcolor: style.bgcolor || 'grey.50',
        borderRadius: '0 4px 4px 0',
      }}
    >
      <Box sx={{ flexShrink: 0, mt: 0.25 }}>{style.icon}</Box>
      <Typography variant="body2">{content}</Typography>
    </Box>
  );
}

// --- TiptapRenderer ---
// Renders a Tiptap JSON document tree into React elements
function TiptapRenderer({ content, onImageClick }) {
  if (!content) return null;

  const renderNode = (node, idx) => {
    if (!node) return null;
    const children = node.content?.map((child, i) => renderNode(child, i)) || [];

    switch (node.type) {
      case 'doc':
        return <Box key={idx}>{children}</Box>;
      case 'paragraph':
        return <Typography key={idx} variant="body2" paragraph>{children.length ? children : '\u00A0'}</Typography>;
      case 'heading': {
        const level = node.attrs?.level || 2;
        const variant = `h${Math.min(level + 3, 6)}`;
        return <Typography key={idx} variant={variant} gutterBottom sx={{ mt: 2 }}>{children}</Typography>;
      }
      case 'bulletList':
        return <Box key={idx} component="ul" sx={{ pl: 3, my: 1 }}>{children}</Box>;
      case 'orderedList':
        return <Box key={idx} component="ol" sx={{ pl: 3, my: 1 }}>{children}</Box>;
      case 'listItem':
        return <Box key={idx} component="li" sx={{ mb: 0.5 }}><Typography variant="body2" component="span">{children}</Typography></Box>;
      case 'codeBlock':
        return (
          <Box key={idx} component="pre" sx={{ bgcolor: 'grey.100', p: 1.5, borderRadius: 1, overflowX: 'auto', my: 1.5 }}>
            <Typography variant="body2" component="code" sx={{ fontFamily: 'monospace' }}>{children}</Typography>
          </Box>
        );
      case 'code':
        return <Typography key={idx} variant="body2" component="code" sx={{ bgcolor: 'grey.100', px: 0.5, borderRadius: 0.5, fontFamily: 'monospace' }}>{children}</Typography>;
      case 'image': {
        const src = node.attrs?.src || '';
        const alt = node.attrs?.alt || '';
        return (
          <Box
            key={idx}
            component="img"
            src={src}
            alt={alt}
            onClick={() => onImageClick && onImageClick(src, alt)}
            sx={{ maxWidth: '100%', height: 'auto', display: 'block', my: 1.5, cursor: 'pointer', borderRadius: 1, border: 1, borderColor: 'divider' }}
          />
        );
      }
      case 'callout':
        return <CalloutBlock key={idx} type={node.attrs?.type || 'tip'} content={node.attrs?.content || children} />;
      case 'hardBreak':
        return <br key={idx} />;
      case 'horizontalRule':
        return <Divider key={idx} sx={{ my: 2 }} />;
      case 'text': {
        let el = node.text || '';
        if (node.marks) {
          node.marks.forEach((mark) => {
            if (mark.type === 'bold') el = <strong key={idx}>{el}</strong>;
            if (mark.type === 'italic') el = <em key={idx}>{el}</em>;
            if (mark.type === 'code') el = <Typography key={idx} component="code" variant="body2" sx={{ bgcolor: 'grey.100', px: 0.5, borderRadius: 0.5, fontFamily: 'monospace' }}>{el}</Typography>;
            if (mark.type === 'link') el = <Typography key={idx} component="a" href={mark.attrs?.href} target="_blank" rel="noopener noreferrer" variant="body2" color="primary">{el}</Typography>;
          });
        }
        return el;
      }
      default:
        return children.length ? <Box key={idx}>{children}</Box> : null;
    }
  };

  return renderNode(content, 0);
}

// --- ArticleView ---
export default function ArticleView() {
  const { activeArticleId, closePanel, setActiveView } = useKnowledgebase();
  const auth = useAuth();
  const history = useHistory();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxAlt, setLightboxAlt] = useState('');

  useEffect(() => {
    if (!activeArticleId) return;
    setLoading(true);
    setError(null);
    getArticle(activeArticleId, auth.activeUser?.accessToken)
      .then((response) => {
        if (response?.data) {
          setArticle(response.data);
        } else {
          setError('Article not found.');
        }
      })
      .catch(() => setError('Failed to load article.'))
      .finally(() => setLoading(false));
  }, [activeArticleId, auth.activeUser?.accessToken]);

  const handleNavLink = (route) => {
    closePanel();
    history.push(route);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" action={<Button size="small" onClick={() => setActiveView('search')}>Back to search</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!article) return null;

  return (
    <Box sx={{ p: 2 }}>
      {/* Back button */}
      <Button
        startIcon={<ArrowBackIcon />}
        size="small"
        onClick={() => setActiveView('search')}
        sx={{ mb: 1.5 }}
      >
        Back
      </Button>

      {/* Title */}
      <Typography variant="h6" gutterBottom>{article.title}</Typography>

      {/* Meta */}
      {article.last_updated && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Last updated: {new Date(article.last_updated).toLocaleDateString()}
        </Typography>
      )}

      {/* Summary */}
      {article.summary && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {article.summary}
        </Typography>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Body */}
      <TiptapRenderer
        content={article.body}
        onImageClick={(src, alt) => { setLightboxSrc(src); setLightboxAlt(alt); }}
      />

      {/* Feedback */}
      <FeedbackWidget contentId={article.id} contentType="article" />

      {/* Image lightbox */}
      <ImageLightbox
        src={lightboxSrc}
        alt={lightboxAlt}
        open={!!lightboxSrc}
        onClose={() => setLightboxSrc(null)}
      />
    </Box>
  );
}
